import React, { useState, useEffect } from 'react';
import { Plus, Eye, EyeOff } from 'lucide-react';
import ProviderCard from './ProviderCard';
import Button from '../ui/Button';
import LoadingSpinner from '../ui/LoadingSpinner';
import { providerTypesService, SupportedProviderType } from '../../services/providerTypes';

interface Provider {
  id: string;
  name: string;
  type: string;
  status: string;
  configuration: Record<string, any>;
  lastSync?: string;
  isLoading?: boolean;
}

interface ProvidersListProps {
  providers: Provider[];
  isLoading: boolean;
  error: string | null;
  onAddProvider: () => void;
  onEditProvider: (provider: Provider) => void;
  onDeleteProvider: (id: string) => void;
  onSyncProvider: (id: string) => void;
  onToggleProviderStatus: (id: string) => void;
}

const ProvidersList: React.FC<ProvidersListProps> = ({
  providers,
  isLoading,
  error,
  onAddProvider,
  onEditProvider,
  onDeleteProvider,
  onSyncProvider,
  onToggleProviderStatus,
}) => {
  const [showAllProviders, setShowAllProviders] = useState(false);
  const [supportedProviders, setSupportedProviders] = useState<SupportedProviderType[]>([]);

  // Load all supported providers when component mounts
  useEffect(() => {
    const loadSupportedProviders = async () => {
      try {
        const allProviders = await providerTypesService.getSupportedProviderTypes();
        setSupportedProviders(allProviders);
      } catch (error) {
        console.error('Failed to load supported providers:', error);
      }
    };
    loadSupportedProviders();
  }, []);
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading providers...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500 mb-4">Error: {error}</div>
        <Button onClick={() => window.location.reload()} variant="outline">
          Retry
        </Button>
      </div>
    );
  }

  // Get providers to display (configured + all supported)
  const displayProviders = showAllProviders
    ? [
        ...providers,
        ...supportedProviders
          .filter(sp => !providers.some(p => p.type === sp.type))
          .map(sp => ({
            id: `unsupported-${sp.type}`,
            name: sp.name,
            type: sp.type,
            status: 'not-configured',
            configuration: {},
            isUnconfigured: true,
            description: sp.description,
            credentialFields: sp.credentialFields
          }))
      ]
    : providers;

  if (providers.length === 0 && !showAllProviders) {
    return (
      <div className="text-center py-12">
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 max-w-md mx-auto">
          <div className="mb-4">
            <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Plus className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No DNS Providers Configured
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Get started by adding your first DNS provider to manage your zones and records.
            </p>
          </div>
          <div className="space-y-3">
            <Button onClick={onAddProvider} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Provider
            </Button>
            <Button 
              onClick={() => setShowAllProviders(true)} 
              variant="outline" 
              className="w-full"
            >
              <Eye className="w-4 h-4 mr-2" />
              View All Supported Providers
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            DNS Providers
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage your DNS provider configurations and sync settings
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button 
            onClick={() => setShowAllProviders(!showAllProviders)}
            variant="outline"
            className="flex items-center"
          >
            {showAllProviders ? (
              <>
                <EyeOff className="w-4 h-4 mr-2" />
                Configured Only
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-2" />
                Show All Supported
              </>
            )}
          </Button>
          <Button onClick={onAddProvider}>
            <Plus className="w-4 h-4 mr-2" />
            Add Provider
          </Button>
        </div>
      </div>

      {/* Providers Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {displayProviders.map((provider) => (
          <ProviderCard
            key={provider.id}
            provider={provider}
            onEdit={provider.isUnconfigured ? () => onAddProvider() : onEditProvider}
            onDelete={provider.isUnconfigured ? () => {} : onDeleteProvider}
            onSync={provider.isUnconfigured ? () => {} : onSyncProvider}
            onToggleStatus={provider.isUnconfigured ? () => {} : onToggleProviderStatus}
            isUnconfigured={provider.isUnconfigured}
          />
        ))}
      </div>

      {/* Stats Summary */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between text-sm">
          <div className="flex space-x-6">
            <span className="text-gray-600 dark:text-gray-400">
              Configured: <span className="font-medium text-gray-900 dark:text-white">{providers.length}</span>
            </span>
            <span className="text-gray-600 dark:text-gray-400">
              Active: <span className="font-medium text-green-600">{providers.filter(p => p.status === 'active').length}</span>
            </span>
            <span className="text-gray-600 dark:text-gray-400">
              Inactive: <span className="font-medium text-yellow-600">{providers.filter(p => p.status === 'inactive').length}</span>
            </span>
            {showAllProviders && (
              <span className="text-gray-600 dark:text-gray-400">
                Supported: <span className="font-medium text-blue-600">{supportedProviders.length}</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProvidersList;